import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";

/**
 * @param {object} query — camelCase per AiCallLogQueryRequestDto
 * @returns {Promise<{ records: object[], totalElements: number, totalPages: number, page: number, first?: boolean, last?: boolean, empty?: boolean }>}
 *    Mirrors backend `com.razormetrics.commons.util.dto.list.v2.ResponseDto<AiCallLogRowDto>`.
 */
export function fetchAiCallLogPage(query) {
  return new Promise((resolve, reject) => {
    sendAuthenticatedAsyncRequest(
      "/ai-vendor/call-log/list",
      "POST",
      query,
      (res) => resolve(res.data),
      (err) => reject(err)
    );
  });
}

/**
 * @typedef {{ phoneNumber: string, active: boolean, blockReason: "INVALID" | "HUMAN_REQUESTED" | null }} PhoneNumberSuppressionResponse
 */

/**
 * Platform expects max length 10. Normalize US numbers that include a leading country code 1.
 * @param {string} phoneNumber
 * @returns {string}
 */
export function normalizeSuppressionPhoneNumber(phoneNumber) {
  const digits = String(phoneNumber ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits || String(phoneNumber ?? "").trim();
}

/**
 * @param {string} phoneNumber
 * @returns {Promise<PhoneNumberSuppressionResponse>}
 */
export function fetchPhoneNumberSuppression(phoneNumber) {
  const normalized = normalizeSuppressionPhoneNumber(phoneNumber);
  const qs = `phoneNumber=${encodeURIComponent(normalized)}`;
  return new Promise((resolve, reject) => {
    sendAuthenticatedAsyncRequest(
      `/ai-vendor/phone-number/suppression?${qs}`,
      "GET",
      null,
      (res) => resolve(res.data),
      (err) => reject(err)
    );
  });
}

/**
 * @param {string} phoneNumber
 * @param {boolean} active — true = block, false = unblock
 * @returns {Promise<PhoneNumberSuppressionResponse>}
 */
export function putPhoneNumberSuppression(phoneNumber, active) {
  const normalized = normalizeSuppressionPhoneNumber(phoneNumber);
  return new Promise((resolve, reject) => {
    sendAuthenticatedAsyncRequest(
      "/ai-vendor/phone-number/suppression",
      "PUT",
      { phoneNumber: normalized, active },
      (res) => resolve(res.data),
      (err) => reject(err)
    );
  });
}

export function buildAiCallLogQuery({
  switchInfoId,
  callGroupTypes,
  outboundCallStatuses,
  switchStatuses,
  outcomeTags,
  outboundPhoneSearch,
  prescriberNpiSearch,
  creationDateFrom,
  creationDateTo,
  updateDateFrom,
  updateDateTo,
  limit,
  offset,
}) {
  const body = { limit, offset };
  if (switchInfoId != null) body.switchInfoId = switchInfoId;
  if (callGroupTypes?.length) body.callGroupTypes = callGroupTypes;
  if (outboundCallStatuses?.length) body.outboundCallStatuses = outboundCallStatuses;
  if (switchStatuses?.length) body.switchStatuses = switchStatuses;
  if (outcomeTags?.length) body.outcomeTags = outcomeTags;
  if (outboundPhoneSearch) body.outboundPhoneSearch = outboundPhoneSearch;
  if (prescriberNpiSearch) body.prescriberNpiSearch = prescriberNpiSearch;
  if (creationDateFrom) body.creationDateFrom = creationDateFrom;
  if (creationDateTo) body.creationDateTo = creationDateTo;
  if (updateDateFrom) body.updateDateFrom = updateDateFrom;
  if (updateDateTo) body.updateDateTo = updateDateTo;
  return body;
}
